import React, { createContext, useCallback, useContext, useMemo, useState } from 'react';
import { Badge } from '@givebutter/primitives/badge';
import { Button } from '@givebutter/primitives/button';
import * as RadioGroup from '@givebutter/primitives/radio-group';
import * as SingleSelect from '@givebutter/primitives/single-select';
import {
    faArrowRight,
    faDownload,
    faEyeSlash,
    faHashtag,
    faPercent,
} from '@fortawesome/pro-regular-svg-icons';
import { faRepeat } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { ACCEPTED_FILE_EXTENSIONS, MAX_FILE_SIZE } from 'components/custom-fields/constants';
import * as CFHelpers from 'components/custom-fields/helpers';
import * as Form from 'components/form';
import { SelectListItem, UseFormSearchableSelect } from 'components/select';
import { UseFormUpload } from 'components/upload';
import { useCustomFieldUrl } from 'customHooks/useCustomFileUrl';
import { formatUploadedFileName } from 'helpers/formatting';
import { twMerge } from 'tailwind-merge';
import { CustomField } from 'types/domain';

interface CustomFieldResponseFormProps {
    field: CustomField;
    children: React.ReactNode;
    onAddFile?: (isAdding: boolean) => void;
}

interface CustomFieldResponseContext {
    field: CustomField | null;
    isAddingFile: boolean;
    setIsAddingFile: (isAdding: boolean) => void;
}

const defaultContext: CustomFieldResponseContext = {
    field: null,
    isAddingFile: false,
    setIsAddingFile: () => {},
};

const CustomFieldResponseContext = createContext<CustomFieldResponseContext>(defaultContext);

const useCustomFieldResponseForm = () => {
    const context = useContext(CustomFieldResponseContext);
    if (!context) {
        return defaultContext;
    }
    return context;
};

const CustomFieldResponseForm = ({
    field,
    children,
    onAddFile,
    ...props
}: CustomFieldResponseFormProps & React.HTMLAttributes<HTMLDivElement>) => {
    const [isAddingFile, setIsAddingFile] = useState(false);

    const value = useMemo(
        () => ({
            field,
            isAddingFile,
            setIsAddingFile: (newValue: boolean) => {
                setIsAddingFile(newValue);
                onAddFile?.(newValue);
            },
        }),
        [field, isAddingFile, onAddFile],
    );

    return (
        <CustomFieldResponseContext.Provider value={value}>
            <div {...props}>{children}</div>
        </CustomFieldResponseContext.Provider>
    );
};

const CustomFieldResponseFormTitle = (props: React.HTMLAttributes<HTMLLabelElement>) => {
    const { field } = useCustomFieldResponseForm();
    const { path } = Form.useGivebutterFormContext();
    if (!field) return null;

    const adminOnly = !field.public;
    const fieldPrefix = 'customFields.field_' + field.id;

    return (
        <Form.Label name={path(fieldPrefix)} className="flex gap-2" {...props}>
            <div>
                {field.title}
                {!!field.required && <Form.LabelAsterisk />}
            </div>
            {adminOnly && (
                <Badge size="sm">
                    <FontAwesomeIcon icon={faEyeSlash} size="xs" />
                    Hidden
                </Badge>
            )}
        </Form.Label>
    );
};

const CustomFieldResponseFormDescription = () => {
    const { field } = useCustomFieldResponseForm();
    if (!field) return null;

    return <p className={'text-sm text-gray-600'}>{field.description}</p>;
};

const CustomFieldResponseFormError = () => {
    const { field } = useCustomFieldResponseForm();
    const { path } = Form.useGivebutterFormContext();
    if (!field) return null;

    const fieldPrefix = 'customFields.field_' + field.id;

    return <Form.Error name={path(fieldPrefix)} />;
};

const CustomFieldResponseFormReadOnlyField = () => {
    const { field } = useCustomFieldResponseForm();
    const fileUrl = useCustomFieldUrl(field?.response?.id);

    const DisplayValue = useCallback(() => {
        if (field?.type === 'file') {
            if (field?.value_friendly) {
                return (
                    <div className="max-w-[350px] overflow-x-auto">
                        <a
                            href={fileUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="whitespace-nowrap text-blue-600 hover:underline"
                        >
                            {formatUploadedFileName(field.value_friendly)}
                        </a>
                    </div>
                );
            } else {
                return <p className="text-gray-300">No file uploaded</p>;
            }
        } else {
            return (
                <p
                    className={twMerge(
                        'text-sm',
                        field?.value_friendly ? 'text-gray-600' : 'text-gray-300',
                    )}
                >
                    {field ? CFHelpers.getReadableDisplayValueByType(field) : 'No answer'}
                </p>
            );
        }
    }, [field, fileUrl]);

    return (
        <p
            className={twMerge(
                'text-sm',
                field?.value_friendly ? 'text-gray-600' : 'text-gray-300',
            )}
        >
            <DisplayValue />
        </p>
    );
};

const CustomFieldResponseFormField = () => {
    const { field, setIsAddingFile } = useCustomFieldResponseForm();
    const [isEditing, setIsEditing] = useState(false);
    const customFieldResponseId = field?.response?.id;
    const fileUrl = useCustomFieldUrl(customFieldResponseId);

    const { control, register, path } = Form.useGivebutterFormContext();
    if (!field) return null;

    const fieldPrefix = 'customFields.field_' + field.id;

    switch (field.type) {
        case 'text':
            return (
                <Form.TextInputRoot name={fieldPrefix} control={control}>
                    <Form.TextInputForm />
                </Form.TextInputRoot>
            );
        case 'textarea':
            return (
                <textarea
                    {...register(fieldPrefix)}
                    className={
                        'w-full resize-none rounded border border-gray-200 px-4 py-2 leading-relaxed'
                    }
                />
            );
        case 'number':
        case 'percentage':
            return (
                <Form.TextInputRoot
                    name={fieldPrefix}
                    control={control}
                    inputRootProps={{
                        mask: 'whole-number',
                    }}
                >
                    <Form.TextInputForm />
                    <FontAwesomeIcon
                        icon={field.type === 'percentage' ? faPercent : faHashtag}
                        className={'text-gray-600'}
                    />
                </Form.TextInputRoot>
            );
        case 'date':
            return (
                <Form.TextInputRoot
                    control={control}
                    name={fieldPrefix}
                    inputRootProps={{
                        mask: 'date',
                    }}
                >
                    <Form.TextInputForm placeholder={'MM/DD/YYYY'} />
                </Form.TextInputRoot>
            );
        case 'daterange':
            return (
                <div className={'grid grid-cols-11'}>
                    <div className={'col-span-5 space-y-0.5'}>
                        <Form.TextInputRoot
                            control={control}
                            name={`${fieldPrefix}.start`}
                            rules={{
                                validate: (value, form) => {
                                    if (
                                        form.customFields[`field_${field.id}`].end?.length &&
                                        !value?.length
                                    ) {
                                        return `${field.title} needs a start and end date.`;
                                    }

                                    return true;
                                },
                            }}
                            inputRootProps={{
                                className: 'flex-grow',
                                mask: 'date',
                            }}
                        >
                            <Form.TextInputForm placeholder={'MM/DD/YYYY'} />
                        </Form.TextInputRoot>
                        <Form.Error name={`${fieldPrefix}.start`} />
                    </div>
                    <div className={'col-span-1 flex justify-center pt-3'}>
                        <FontAwesomeIcon icon={faArrowRight} />
                    </div>
                    <div className={'col-span-5 space-y-0.5'}>
                        <Form.TextInputRoot
                            control={control}
                            name={`${fieldPrefix}.end`}
                            rules={{
                                validate: (value, form) => {
                                    if (
                                        form.customFields[`field_${field.id}`].start?.length &&
                                        !value?.length
                                    ) {
                                        return `${field.title} needs a start and end date.`;
                                    }

                                    return true;
                                },
                            }}
                            inputRootProps={{
                                className: 'flex-grow',
                                mask: 'date',
                            }}
                        >
                            <Form.TextInputForm placeholder={'MM/DD/YYYY'} />
                        </Form.TextInputRoot>
                        <Form.Error name={`${fieldPrefix}.end`} />
                    </div>
                </div>
            );
        case 'checkbox':
            return (
                <div>
                    <Form.UseFormCheckboxInput control={control} name={fieldPrefix} />
                </div>
            );

        case 'select':
            return (
                <Form.SingleSelect name={fieldPrefix} control={control}>
                    <SingleSelect.Trigger aria-label="Select an option" />
                    <SingleSelect.Content>
                        {field.answers?.map(answer => (
                            <SingleSelect.Item key={answer} value={answer}>
                                <SingleSelect.ItemText>{answer}</SingleSelect.ItemText>
                            </SingleSelect.Item>
                        ))}
                    </SingleSelect.Content>
                </Form.SingleSelect>
            );
        case 'multiselect':
            return (
                <UseFormSearchableSelect
                    name={fieldPrefix}
                    control={control}
                    multi={field.type === 'multiselect'}
                    showTags={field.type === 'multiselect'}
                >
                    {field.answers?.map(answer => (
                        <SelectListItem
                            key={answer}
                            value={answer}
                            checkable={field.type === 'multiselect'}
                        >
                            {answer}
                        </SelectListItem>
                    ))}
                </UseFormSearchableSelect>
            );
        case 'radio':
            return (
                <Form.UseFormRadioGroup name={fieldPrefix} control={control}>
                    {field.answers?.map((answer, index) => {
                        const id = `${answer}-${index}`;
                        return (
                            <RadioGroup.ItemContainer key={answer}>
                                <RadioGroup.Item id={id} value={answer}>
                                    <RadioGroup.Indicator />
                                </RadioGroup.Item>
                                <Form.Label
                                    name={path(fieldPrefix)}
                                    htmlFor={id}
                                    className={'leading-none'}
                                >
                                    {answer}
                                </Form.Label>
                            </RadioGroup.ItemContainer>
                        );
                    })}
                </Form.UseFormRadioGroup>
            );
        case 'file':
            if (field.response?.value && customFieldResponseId && !isEditing) {
                return (
                    <div className="flex gap-1 pt-2">
                        <a href={fileUrl} target="_blank" rel="noopener noreferrer">
                            <Button variant={'outline'} size={'sm'}>
                                <FontAwesomeIcon icon={faDownload} className="flex-shrink-0" />
                                <div className="flex-grow overflow-x-auto">
                                    <p className="max-w-[250px] overflow-ellipsis whitespace-nowrap">
                                        {formatUploadedFileName(`${field.response?.value}`)}
                                    </p>
                                </div>
                            </Button>
                        </a>
                        <div>
                            <Button
                                onClick={() => {
                                    setIsEditing(true);
                                }}
                                size="sm"
                                variant={'outline'}
                            >
                                <FontAwesomeIcon icon={faRepeat} />
                            </Button>
                        </div>
                    </div>
                );
            }

            return (
                <div className="flex flex-col gap-2">
                    <UseFormUpload
                        name={fieldPrefix}
                        variant={'single'}
                        acceptedFileTypes={[
                            'image/*',
                            'text/csv',
                            'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
                            'text/plain',
                            'application/msword',
                            'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
                            'application/vnd.openxmlformats-officedocument.presentationml.presentation',
                            'application/pdf',
                            'text/rtf',
                        ]}
                        allowMultiple={false}
                        allowReplace={true}
                        control={control}
                        // @ts-ignore
                        allowRemove={true}
                        imagePreviewHeight={200}
                        uploadLabelText="Click to add file"
                        maxFileSize={`${MAX_FILE_SIZE}mb`}
                        onaddfilestart={() => setIsAddingFile(true)}
                        onupdatefiles={() => setIsAddingFile(false)}
                    />
                    <div className="flex flex-col">
                        <span className={'text-sm text-gray-600'}>
                            Maximum upload file size: {MAX_FILE_SIZE}MB
                        </span>
                        <span className={'text-sm text-gray-600'}>
                            Accepted file types: {ACCEPTED_FILE_EXTENSIONS}
                        </span>
                    </div>
                </div>
            );
        default:
            return null;
    }
};

const Root = CustomFieldResponseForm;
const Title = CustomFieldResponseFormTitle;
const Input = CustomFieldResponseFormField;
const ReadOnlyInput = CustomFieldResponseFormReadOnlyField;
const Description = CustomFieldResponseFormDescription;
const FormError = CustomFieldResponseFormError;

export {
    useCustomFieldResponseForm,
    //
    Root,
    Title,
    Input,
    ReadOnlyInput,
    Description,
    FormError,
    //
    CustomFieldResponseForm,
    CustomFieldResponseFormTitle,
    CustomFieldResponseFormField,
    CustomFieldResponseFormDescription,
    CustomFieldResponseFormError,
    CustomFieldResponseFormReadOnlyField,
};
